import { useEffect, useRef, useState } from 'react'

export interface TrackingProvider {
  track: (eventName: string, meta?: Record<string, any>) => void
}

const providers: TrackingProvider[] = []

let sessionInfo: Record<string, any> = {}

/**
 * Register a new analytics/event tracking provider
 */
export function registerTrackingProvider(provider: TrackingProvider): void {
  providers.push(provider)
}

export const setSessionInfo = (info: Record<string, any>): void => {
  sessionInfo = { ...sessionInfo, ...info }
}

export const updateSessionInfo = (key: string, value: any): void => {
  sessionInfo[key] = value
}

export const clearSessionInfo = (): void => {
  sessionInfo = {}
}

export const getSessionInfo = (): Record<string, any> => ({ ...sessionInfo })

export enum InputMethod {
  TYPING = 'typing',
  SPEECH_TO_TEXT = 'speech_to_text',
  SUGGESTED_PROMPT = 'suggested_prompt',
  INGRESS = 'ingress',
}

export enum ChatContext {
  NEW_CHAT = 'new_chat',
  CURRENT_CHAT = 'current_chat',
  HISTORIC_CHAT = 'historic_chat',
}

export enum TrackingEvent {
  AI_ASSISTANT_OPENED = 'AiAssistantOpened',
  AI_ASSISTANT_CLOSED = 'AiAssistantClosed',
  AI_ASSISTANT_MESSAGE_SENT = 'AiAssistantMessageSent',
  AI_ASSISTANT_SPEECH_TO_TEXT_COMPLETED = 'AiAssistantSpeechToTextCompleted',
  AI_ASSISTANT_RESPONSE_LINK_CLICKED = 'AiAssistantResponseLinkClicked',
  AI_ASSISTANT_SESSION_CREATED = 'AiAssistantSessionCreated',
  AI_ASSISTANT_SESSION_SWITCHED = 'AiAssistantSessionSwitched',
  AI_ASSISTANT_SESSION_DELETED = 'AiAssistantSessionDeleted',
  AI_ASSISTANT_SESSIONS_LOADED = 'AiAssistantSessionsLoaded',
  AI_ASSISTANT_RESPONSE_FIRST_TOKEN = 'AiAssistantResponseFirstToken',
  AI_ASSISTANT_RESPONSE_COMPLETE = 'AiAssistantResponseComplete',
  AI_ASSISTANT_RESPONSE_CONTAINS_ERROR = 'AiAssistantResponseContainsError',
  AI_ASSISTANT_FEEDBACK_POSITIVE = 'AiAssistantFeedbackPositive',
  AI_ASSISTANT_FEEDBACK_NEGATIVE = 'AiAssistantFeedbackNegative',
  AI_ASSISTANT_AVAILABLE = 'AiAssistantAvailable',
  CREATE_SOAP = 'CreateSoap',
  OPEN_EDIT_APPOINTMENT_DIALOG = 'OpenEditAppointmentDialog',
  OPEN_ADD_APPOINTMENT_DIALOG = 'OpenAddAppointmentDialog',
  CLICK_SAVE_APPOINTMENT = 'ClickSaveAppointment',
  APPOINTMENT_VALIDATION_ERROR = 'AppointmentValidationError',
  BYPASS_APPOINTMENT_VALIDATION = 'BypassAppointmentValidation',
  PATCH_APPOINTMENT_REQUEST = 'PatchAppointmentRequest',
  SEARCH_CHARGE_TAB = 'SearchChargeTab',
  SEARCH_CHARGE_RESULTS = 'SearchChargeResults',
  SEARCH_CHARGE_CLICK_RESULT = 'SearchChargeClickResult',
  SEARCH_CHARGE_NO_SELECTION = 'SearchChargeNoSelection',
  SESSION_INFO = 'SessionInfo',
  SWAP_PRODUCT_BUTTON = 'SwapProductButton',
  SWAP_COUNT_BUTTON = 'SwapCountButton',
  SWAP_PATIENT_BUTTON = 'SwapPatientButton',
  SWAP_PRODUCT_SEARCH_SELECT_PRODUCT = 'SwapProductSearchSelectProduct',
  SWAP_PRODUCT_SEARCH_CANCEL = 'SwapProductSearchCancel',
  SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK = 'SwapProductSearchCancelGoBack',
  SWAP_PRODUCT_SEARCH_CANCEL_CLOSE = 'SwapProductSearchCancelClose',
  SWAP_PRODUCT_SEARCH_CANCEL_DISCARD = 'SwapProductSearchCancelDiscard',
  SWAP_PRODUCT_RX_FORM_APPROVE = 'SwapProductRxFormApprove',
  SWAP_PRODUCT_RX_FORM_CANCEL = 'SwapProductRxFormCancel',
  SWAP_PRODUCT_FINALIZE_CANCEL = 'SwapProductFinalizeCancel',
  SWAP_PRODUCT_FINALIZE_CONFIRM = 'SwapProductFinalizeConfirm',
  SWAP_COUNT_RX_FORM_APPROVE = 'SwapCountRxFormApprove',
  SWAP_COUNT_RX_FORM_CANCEL = 'SwapCountRxFormCancel',
  SWAP_COUNT_FINALIZE_CANCEL = 'SwapCountFinalizeCancel',
  SWAP_COUNT_FINALIZE_CONFIRM = 'SwapCountFinalizeConfirm',
  SWAP_PATIENT_SEARCH_CANCEL = 'SwapPatientSearchCancel',
  SWAP_PATIENT_SEARCH_SELECT_PATIENT = 'SwapPatientSearchSelectPatient',
  SWAP_PATIENT_RX_FORM_APPROVE = 'SwapPatientRxFormApprove',
  SWAP_PATIENT_RX_FORM_CANCEL = 'SwapPatientRxFormCancel',
  SWAP_PATIENT_FINALIZE_CANCEL = 'SwapPatientFinalizeCancel',
  SWAP_PATIENT_FINALIZE_CONFIRM = 'SwapPatientFinalizeConfirm',
  DECLINE_PET_NEEDS_SEEN = 'DeclinePetNeedsSeen',
  DECLINE_WRONG_PRODUCT = 'DeclineWrongProduct',
  DECLINE_WRONG_PET = 'DeclineWrongPet',
  DECLINE_NOT_A_CLINIC_PATIENT = 'DeclineNotAClinicPatient',
  DECLINE_OTHER = 'DeclineOther',
  APPROVE_REACTIVE_RX = 'ApproveReactiveRx',
  ADD_PAYMENT_V2_UNLINKED_REFUND_STARTED = 'AddPaymentV2UnlinkedRefundStarted',
  ADD_PAYMENT_V2_UNLINKED_REFUND_PROCESSING = 'AddPaymentV2UnlinkedRefundProcessing',
  ADD_PAYMENT_V2_UNLINKED_REFUND_SUCCESS = 'AddPaymentV2UnlinkedRefundSuccess',
  ADD_PAYMENT_V2_UNLINKED_REFUND_FAILURE = 'AddPaymentV2UnlinkedRefundFailure',
  ADD_PAYMENT_V2_UNLINKED_REFUND_HARD_FAILURE = 'AddPaymentV2UnlinkedRefundHardFailure',
  ADD_PAYMENT_V2_UNLINKED_REFUND_INITIATION_ERROR = 'AddPaymentV2UnlinkedRefundInitiationError',
  ADD_PAYMENT_V2_LINKED_REFUND_STARTED = 'AddPaymentV2LinkedRefundStarted',
  ADD_PAYMENT_V2_LINKED_REFUND_PROCESSING = 'AddPaymentV2LinkedRefundProcessing',
  ADD_PAYMENT_V2_LINKED_REFUND_SUCCESS = 'AddPaymentV2LinkedRefundSuccess',
  ADD_PAYMENT_V2_LINKED_REFUND_FAILURE = 'AddPaymentV2LinkedRefundFailure',
  ADD_PAYMENT_V2_LINKED_REFUND_HARD_FAILURE = 'AddPaymentV2LinkedRefundHardFailure',
  ADD_PAYMENT_V2_LINKED_REFUND_INITIATION_ERROR = 'AddPaymentV2LinkedRefundInitiationError',
  ADD_PAYMENT_V2_REFUND_INVOICE_REFETCH = 'AddPaymentV2RefundInvoiceRefetch',
  ADD_PAYMENT_V2_REVERSE_TRANSACTION_STARTED = 'AddPaymentV2ReverseTransactionStarted',
  ADD_PAYMENT_V2_REVERSE_TRANSACTION_PROCESSING = 'AddPaymentV2ReverseTransactionProcessing',
  ADD_PAYMENT_V2_REVERSE_TRANSACTION_INITIATION_ERROR = 'AddPaymentV2ReverseTransactionInitiationError',
  ADD_PAYMENT_V2_VOID_SUCCESS = 'AddPaymentV2VoidSuccess',
  ADD_PAYMENT_V2_VOID_FAILURE = 'AddPaymentV2VoidFailure',
  ADD_PAYMENT_V2_REVERSE_SUCCESS = 'AddPaymentV2ReverseSuccess',
  ADD_PAYMENT_V2_REVERSE_FAILURE = 'AddPaymentV2ReverseFailure',
  ADD_PAYMENT_V2_SEND_TO_TERMINAL_CLICKED = 'AddPaymentV2SendToTerminalClicked',
  ADD_PAYMENT_V2_SEND_TO_TERMINAL_INITIATED = 'AddPaymentV2SendToTerminalInitiated',
  ADD_PAYMENT_V2_SEND_TO_TERMINAL_INITIATED_SUCCESS = 'AddPaymentV2SendToTerminalInitiatedSuccess',
  ADD_PAYMENT_V2_TERMINAL_SUCCESS = 'AddPaymentV2TerminalSuccess',
  ADD_PAYMENT_V2_TERMINAL_FAILURE = 'AddPaymentV2TerminalFailure',
  ADD_PAYMENT_V2_INVOICE_REFRESHED = 'AddPaymentV2InvoiceRefreshed',
  ADD_PAYMENT_V2_FLOW_ERROR = 'AddPaymentV2FlowError',
}

export enum SearchChargeNoSelectionReason {
  SEARCH_TERM_UPDATED = 'search_term_updated',
  SEARCH_TERM_CLEARED = 'search_term_cleared',
  NAVIGATED_AWAY = 'navigated_away',
}

/**
 * Track an event using all registered providers
 */
export const trackEvent = (
  eventName: TrackingEvent,
  meta: Record<string, any> = {},
): void => {
  // setTimeout ensures that the event tracking is not blocking the main thread
  setTimeout(() => {
    // Merge session info with event-specific meta, prefixing session keys with "session_"
    const enrichedMeta = {
      ...Object.fromEntries(
        Object.entries(sessionInfo).map(([key, value]) => [
          `session_${key}`,
          value,
        ]),
      ),
      ...meta,
    }

    providers.forEach((provider) => {
      try {
        provider.track(eventName, enrichedMeta)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Tracking error for ${eventName}:`, err)
      }
    })
  }, 0)
}

/**
 * Tracks an event only once when the component mounts.
 */
export const useTrackMount = (
  eventName: TrackingEvent,
  meta: Record<string, any> = {},
): void => {
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackEvent(eventName, meta)
      hasTrackedRef.current = true
    }
  }, [eventName, meta])
}

export const useTrackSearch = (
  eventName: TrackingEvent,
  selectedItemKey: string,
): {
  addSearchAttempt: (searchAttempt: string) => void
  startSearch: () => void
  trackSearch: (
    selectedItem: string | undefined,
    meta?: Record<string, any>,
  ) => void
} => {
  const [searchAttempts, setSearchAttempts] = useState<string[]>([])
  const [searchStarted, setSearchStarted] = useState<Date | null>(null)

  const startSearch = () => {
    if (searchStarted === null) {
      setSearchStarted(new Date())
    }
  }

  const addSearchAttempt = (searchAttempt: string) => {
    startSearch()
    setSearchAttempts((prev) => [...prev, searchAttempt])
  }

  const trackSearch = (
    selectedItem: string | undefined,
    meta: Record<string, any> = {},
  ) => {
    trackEvent(eventName, {
      [selectedItemKey]: selectedItem,
      searchAttempts,
      searchDuration: searchStarted
        ? new Date().getTime() - searchStarted.getTime()
        : undefined,
      ...meta,
    })
    setSearchAttempts([])
    setSearchStarted(null)
  }

  return {
    startSearch,
    addSearchAttempt,
    trackSearch,
  }
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const NINETY_DAYS_MS = 90 * ONE_DAY_MS
const CLEANUP_CHECK_KEY = 'tracked_cleanup_last_run'

/**
 * Cleans up localStorage entries for tracking events that are older than 90 days.
 * Runs at most once per day to avoid performance issues.
 */
const cleanupOldTrackingEntries = (): void => {
  try {
    const now = Date.now()
    const lastCleanup = localStorage.getItem(CLEANUP_CHECK_KEY)
    const lastCleanupTime = lastCleanup ? parseInt(lastCleanup, 10) : 0

    // Only run cleanup once per day
    if (lastCleanup && now - lastCleanupTime < ONE_DAY_MS) {
      return
    }

    const keysToRemove: string[] = []
    const cutoffTime = now - NINETY_DAYS_MS

    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('tracked_') && key !== CLEANUP_CHECK_KEY) {
        const timestamp = localStorage.getItem(key)
        if (timestamp) {
          const timestampValue = parseInt(timestamp, 10)
          if (!Number.isNaN(timestampValue) && timestampValue < cutoffTime) {
            keysToRemove.push(key)
          }
        }
      }
    }

    // Remove old entries
    keysToRemove.forEach((key) => localStorage.removeItem(key))

    // Update last cleanup time
    localStorage.setItem(CLEANUP_CHECK_KEY, now.toString())
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error cleaning up old tracking entries:', err)
  }
}

/**
 * Tracks an event only once per day per user.
 * Uses localStorage to track the last time the event was tracked.
 * Storage key is generated from the event name and user_id.
 * Automatically cleans up entries older than 90 days.
 *
 * @param eventName - The tracking event to track
 * @param meta - Event metadata, must include user_id for per-user tracking
 */
export const trackEventOnceDaily = (
  eventName: TrackingEvent,
  meta: Record<string, any> = {},
): void => {
  const userId = meta.user_id || sessionInfo.userId
  if (!userId) {
    // Fall back to regular tracking if no user_id provided
    trackEvent(eventName, meta)
    return
  }

  // Clean up old entries (runs at most once per day)
  cleanupOldTrackingEntries()

  const storageKey = `tracked_${eventName}_${userId}`
  const lastTracked = localStorage.getItem(storageKey)
  const now = Date.now()

  // Track if we haven't tracked today (or ever)
  if (!lastTracked || now - parseInt(lastTracked, 10) >= ONE_DAY_MS) {
    trackEvent(eventName, meta)
    localStorage.setItem(storageKey, now.toString())
  }
}
